import { Activity, Check, ChevronsUpDown, Minus, Plus, Power, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@web/components/ui/button";
import { Input } from "@web/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { cn } from "@web/lib/utils";
import type { SourceManifest } from "../api";
import { allSourceGroupId, getMaxSourceGroupCount, getSourceGroupLabels, type SourceGroupTab } from "./source-groups";

export interface SourceManagementProps {
  groups: SourceGroupTab[];
  enabledSourceIds: string[];
  busy: boolean;
  actionBusyId: string | null;
  onCheckSources: (sources: SourceManifest[]) => Promise<void>;
  onSetSourcesEnabled: (sources: SourceManifest[], enabled: boolean) => Promise<void>;
  onCreateGroup: (label: string, sources: SourceManifest[]) => Promise<void>;
  onDeleteGroup: (groupId: string) => Promise<void>;
  onUpdateGroupSourceIds: (groupId: string, sourceIds: string[]) => Promise<void>;
  onSaveGroups: (groups: SourceGroupTab[]) => Promise<void>;
}

export function SourceManagementPanel({
  groups,
  enabledSourceIds,
  busy,
  actionBusyId,
  onCheckSources,
  onSetSourcesEnabled,
  onCreateGroup,
  onDeleteGroup,
  onUpdateGroupSourceIds,
  onSaveGroups,
  className = "",
}: SourceManagementProps & {
  className?: string;
}) {
  const allSources = useMemo(() => groups.find(group => group.id === allSourceGroupId)?.sources ?? [], [groups]);
  const [activeGroupId, setActiveGroupId] = useState(allSourceGroupId);
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [targetGroupId, setTargetGroupId] = useState("");
  const [sourceQuery, setSourceQuery] = useState("");
  const [deleteConfirming, setDeleteConfirming] = useState(false);
  const [localBusyKey, setLocalBusyKey] = useState<string | null>(null);
  const activeGroup = groups.find(group => group.id === activeGroupId) ?? groups[0];
  const visibleSources = activeGroup?.sources ?? [];
  const searchedSources = filterSources(visibleSources, sourceQuery);
  const visibleSelectedSources = searchedSources.filter(source => selectedSourceIds.includes(source.id));
  const editableGroups = groups.filter(group => group.id !== allSourceGroupId);
  const targetGroup = editableGroups.find(group => group.id === targetGroupId) ?? editableGroups[0];
  const disabled = busy || Boolean(actionBusyId) || Boolean(localBusyKey);
  const groupCreateDisabled = !newGroupName.trim() || groups.length >= getMaxSourceGroupCount();

  useEffect(() => {
    const visibleSourceIds = new Set(allSources.map(source => source.id));
    setSelectedSourceIds(current => current.filter(sourceId => visibleSourceIds.has(sourceId)));
  }, [allSources]);

  useEffect(() => {
    if (groups.some(group => group.id === activeGroupId)) return;
    setActiveGroupId(allSourceGroupId);
  }, [activeGroupId, groups]);

  useEffect(() => {
    setDeleteConfirming(false);
  }, [activeGroupId]);

  useEffect(() => {
    if (!targetGroup || targetGroupId === targetGroup.id) return;
    setTargetGroupId(targetGroup.id);
  }, [targetGroup, targetGroupId]);

  async function runAction(key: string, action: () => Promise<void>, requireSelection = true) {
    if (disabled || requireSelection && visibleSelectedSources.length === 0) return;
    setLocalBusyKey(key);
    try {
      await action();
    } finally {
      setLocalBusyKey(null);
    }
  }

  function toggleSource(sourceId: string) {
    setSelectedSourceIds(current => (current.includes(sourceId) ? current.filter(item => item !== sourceId) : [...current, sourceId]));
  }

  function handleSelectAll() {
    const nextIds = new Set(selectedSourceIds);
    for (const source of searchedSources) {
      nextIds.add(source.id);
    }
    setSelectedSourceIds([...nextIds]);
  }

  function handleInvertSelection() {
    const selected = new Set(selectedSourceIds);
    const visibleSourceIds = new Set(searchedSources.map(source => source.id));
    const nextIds = allSources
      .filter(source => (!visibleSourceIds.has(source.id) && selected.has(source.id)) || (visibleSourceIds.has(source.id) && !selected.has(source.id)))
      .map(source => source.id);
    setSelectedSourceIds(nextIds);
  }

  async function handleCreateGroup() {
    await onCreateGroup(newGroupName, []);
    setNewGroupName("");
    setAddingGroup(false);
  }

  async function handleRemoveFromActiveGroup() {
    if (!activeGroup || activeGroup.id === allSourceGroupId || visibleSelectedSources.length === 0) return;
    const removedSourceIds = new Set(visibleSelectedSources.map(source => source.id));
    await onUpdateGroupSourceIds(activeGroup.id, activeGroup.sourceIds.filter(sourceId => !removedSourceIds.has(sourceId)));
    setSelectedSourceIds(current => current.filter(sourceId => !removedSourceIds.has(sourceId)));
  }

  async function handleAddToTargetGroup() {
    if (!targetGroup || visibleSelectedSources.length === 0) return;
    const nextSourceIds = [...new Set([...targetGroup.sourceIds, ...visibleSelectedSources.map(source => source.id)])];
    await onUpdateGroupSourceIds(targetGroup.id, nextSourceIds);
  }

  async function handleMoveToTargetGroup() {
    if (!targetGroup || visibleSelectedSources.length === 0) return;
    const movingSourceIds = new Set(visibleSelectedSources.map(source => source.id));
    const nextGroups = groups.map(group => {
      if (group.id === allSourceGroupId) return group;
      if (group.id === targetGroup.id) {
        return {
          ...group,
          sourceIds: [...new Set([...group.sourceIds, ...movingSourceIds])],
        };
      }

      return {
        ...group,
        sourceIds: group.sourceIds.filter(sourceId => !movingSourceIds.has(sourceId)),
      };
    });
    await onSaveGroups(nextGroups);
  }

  async function handleDeleteActiveGroup() {
    if (!activeGroup || activeGroup.id === allSourceGroupId) return;
    if (!deleteConfirming) {
      setDeleteConfirming(true);
      return;
    }
    await onDeleteGroup(activeGroup.id);
    setDeleteConfirming(false);
  }

  return (
    <div className={`flex min-h-0 flex-col ${className}`}>
      <div className="shrink-0 space-y-4 border-b p-4">
        <section className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <SourceGroupPicker
            value={activeGroup?.id ?? allSourceGroupId}
            groups={groups}
            showCount
            className="w-full sm:hidden"
            onValueChange={setActiveGroupId}
          />

          <div className="scroll-area hidden min-w-0 flex-1 gap-2 overflow-x-auto pb-1 sm:flex">
            {groups.map(group => {
              const active = group.id === activeGroupId;

              return (
                <button
                  key={group.id}
                  type="button"
                  className={`flex h-9 min-w-32 shrink-0 items-center justify-between gap-3 rounded-md border px-3 text-left text-sm transition-colors ${
                    active ? "border-primary bg-primary text-primary-foreground shadow-xs" : "bg-background hover:bg-accent hover:text-accent-foreground"
                  }`}
                  onClick={() => setActiveGroupId(group.id)}
                >
                  <span className="truncate font-medium">{group.label}</span>
                  <span className={active ? "text-primary-foreground/80" : "text-muted-foreground"}>{group.sources.length}</span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" disabled={groups.length >= getMaxSourceGroupCount()} onClick={() => setAddingGroup(true)}>
              <Plus className="size-4" />
              新建分组
            </Button>
            <Button
              type="button"
              size="sm"
              variant={deleteConfirming ? "destructive" : "outline"}
              disabled={!activeGroup || activeGroup.id === allSourceGroupId}
              onClick={() => void handleDeleteActiveGroup()}
              title={deleteConfirming ? "再点一次确认删除" : "删除当前分组"}
            >
              <Minus className="size-4" />
              {deleteConfirming ? "确认删除" : "删除分组"}
            </Button>
          </div>
        </section>

        {addingGroup && (
          <form
            className="flex max-w-md items-center gap-2"
            onSubmit={event => {
              event.preventDefault();
              if (!groupCreateDisabled) void handleCreateGroup();
            }}
          >
            <Input autoFocus value={newGroupName} placeholder="输入分组名" onChange={event => setNewGroupName(event.target.value)} />
            <Button type="submit" size="sm" disabled={groupCreateDisabled}>
              添加
            </Button>
            <Button type="button" size="icon-sm" variant="ghost" onClick={() => setAddingGroup(false)} aria-label="取消新增分组">
              <X className="size-4" />
            </Button>
          </form>
        )}

        <section className="rounded-lg border bg-muted/20 p-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="space-y-1">
              <h3 className="text-sm font-medium">{activeGroup?.label ?? "全部"}书源</h3>
              <p className="text-xs text-muted-foreground">
                当前显示 {searchedSources.length} 个，已选择 {visibleSelectedSources.length} 个
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={handleSelectAll}>
                全选
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={handleInvertSelection}>
                反选
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setSelectedSourceIds([])}>
                清空
              </Button>
            </div>
          </div>

          <div className="grid gap-2 lg:grid-cols-[minmax(14rem,1fr)_auto]">
            <Input value={sourceQuery} placeholder="搜索书源" onChange={event => setSourceQuery(event.target.value)} />
            <div className="flex flex-wrap gap-2">
              <SourceGroupPicker
                value={targetGroup?.id ?? ""}
                groups={editableGroups}
                placeholder="选择分组"
                emptyText="没有分组"
                disabled={editableGroups.length === 0}
                className="min-w-40"
                onValueChange={setTargetGroupId}
              />
              <Button type="button" size="sm" variant="secondary" disabled={disabled || visibleSelectedSources.length === 0 || !targetGroup} onClick={() => runAction("add-group", handleAddToTargetGroup)}>
                添加到分组
              </Button>
              <Button type="button" size="sm" variant="secondary" disabled={disabled || visibleSelectedSources.length === 0 || !targetGroup} onClick={() => runAction("move-group", handleMoveToTargetGroup)}>
                移动到分组
              </Button>
              {activeGroup?.id !== allSourceGroupId && (
                <Button type="button" size="sm" variant="secondary" disabled={disabled || visibleSelectedSources.length === 0} onClick={() => runAction("remove", handleRemoveFromActiveGroup)}>
                  移出当前组
                </Button>
              )}
              <Button type="button" size="sm" variant="outline" disabled={disabled || visibleSelectedSources.length === 0} onClick={() => runAction("check", () => onCheckSources(visibleSelectedSources))}>
                <Activity className={`size-4 ${localBusyKey === "check" ? "animate-spin" : ""}`} />
                检查
              </Button>
              <Button type="button" size="sm" variant="outline" disabled={disabled || visibleSelectedSources.length === 0} onClick={() => runAction("enable", () => onSetSourcesEnabled(visibleSelectedSources, true))}>
                <Power className={`size-4 ${localBusyKey === "enable" ? "animate-pulse" : ""}`} />
                启用
              </Button>
              <Button type="button" size="sm" variant="outline" disabled={disabled || visibleSelectedSources.length === 0} onClick={() => runAction("disable", () => onSetSourcesEnabled(visibleSelectedSources, false))}>
                <Power className={`size-4 ${localBusyKey === "disable" ? "animate-pulse" : ""}`} />
                停用
              </Button>
            </div>
          </div>
        </section>
      </div>

      <div className="scroll-area min-h-0 flex-1 overflow-y-auto p-4">
        <div className="overflow-hidden rounded-lg border bg-background">
          {searchedSources.map(source => {
            const selected = selectedSourceIds.includes(source.id);
            const enabled = enabledSourceIds.includes(source.id);
            const groupLabels = getSourceGroupLabels(source.id, groups);

            return (
              <label key={source.id} className="grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border-b px-3 py-2.5 last:border-b-0 hover:bg-accent/40">
                <input
                  type="checkbox"
                  checked={selected}
                  className="size-4 accent-primary"
                  onChange={() => toggleSource(source.id)}
                />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{source.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{source.id}</span>
                </span>
                <span className="flex flex-wrap items-center justify-end gap-1.5">
                  {groupLabels.map(label => (
                    <span key={label} className="rounded-md border bg-muted/40 px-1.5 py-0.5 text-[11px] text-muted-foreground">
                      {label}
                    </span>
                  ))}
                  <span className={`rounded-md px-2 py-0.5 text-[11px] ${enabled ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                    {enabled ? "启用中" : "已停用"}
                  </span>
                </span>
              </label>
            );
          })}
          {searchedSources.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">没有匹配的书源</div>
          )}
        </div>
      </div>
    </div>
  );
}

function filterSources(sources: SourceManifest[], query: string): SourceManifest[] {
  const keyword = query.trim().toLocaleLowerCase();
  if (!keyword) return sources;

  return sources.filter(source => `${source.name} ${source.id}`.toLocaleLowerCase().includes(keyword));
}

function SourceGroupPicker({
  value,
  groups,
  placeholder = "选择分组",
  emptyText = "没有分组",
  showCount = false,
  disabled = false,
  className,
  onValueChange,
}: {
  value: string;
  groups: SourceGroupTab[];
  placeholder?: string;
  emptyText?: string;
  showCount?: boolean;
  disabled?: boolean;
  className?: string;
  onValueChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedGroup = groups.find(group => group.id === value);

  function handleSelect(groupId: string) {
    onValueChange(groupId);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn("justify-between px-3 font-normal", className)}
        >
          <span className="min-w-0 truncate">
            {selectedGroup ? formatGroupPickerLabel(selectedGroup, showCount) : placeholder}
          </span>
          <ChevronsUpDown className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-1">
        <div className="scroll-area max-h-64 overflow-y-auto">
          {groups.length === 0 && <div className="px-2 py-1.5 text-sm text-muted-foreground">{emptyText}</div>}
          {groups.map(group => {
            const selected = group.id === value;

            return (
              <button
                key={group.id}
                type="button"
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-hidden hover:bg-accent hover:text-accent-foreground",
                  selected && "bg-accent text-accent-foreground",
                )}
                onClick={() => handleSelect(group.id)}
              >
                <span className="min-w-0 truncate">{formatGroupPickerLabel(group, showCount)}</span>
                {selected && <Check className="size-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function formatGroupPickerLabel(group: SourceGroupTab, showCount: boolean): string {
  return showCount ? `${group.label} · ${group.sources.length}` : group.label;
}
